import React, { Suspense } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { LoadScript } from '@react-google-maps/api'
import { ToastContainer } from 'react-toastify'
import { GOOGLE_MAPS_API_KEY } from '@/constant/constant.ts'
import { useMapsConfig } from '@/hooks/useMapsConfig'
import { queryClient } from '@/api/queryClient.ts'
import FullPageLoading from '@/component/util/FullPageLoading'
import { MapProvider, useMapType } from '@/component/map/MapContext'
import BaiduMapLoader from '@/component/map/baidu/BaiduMapLoader'
import 'react-toastify/dist/ReactToastify.css'

interface AppProvidersProps {
    children: React.ReactNode
}

const MapLoaderSwitch: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { region, language } = useMapsConfig()
    const { mapType } = useMapType()

    if (mapType === 'baidu') {
        return (
            <BaiduMapLoader loadingElement={<FullPageLoading />}>
                {/* Google Maps도 백그라운드 로드 (전환 시 즉시 사용 가능) */}
                <LoadScript
                    googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                    region={region}
                    language={language}
                    loadingElement={<></>}
                >
                    {children}
                </LoadScript>
            </BaiduMapLoader>
        )
    }

    return (
        <LoadScript
            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
            region={region}
            language={language}
            loadingElement={<FullPageLoading />}
        >
            {children}
        </LoadScript>
    )
}

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
    return (
        <Suspense fallback={<FullPageLoading />}>
            <MapProvider>
                <MapLoaderSwitch>
                    <div className="font-sans">
                        <ToastContainer
                            position="bottom-right"
                            autoClose={3000}
                            newestOnTop={false}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss={false}
                            draggable
                            pauseOnHover
                            theme="light"
                            stacked
                        />
                        <QueryClientProvider client={queryClient}>
                            <BrowserRouter
                                future={{
                                    v7_startTransition: true,
                                    v7_relativeSplatPath: true
                                }}>
                                {children}
                            </BrowserRouter>
                        </QueryClientProvider>
                    </div>
                </MapLoaderSwitch>
            </MapProvider>
        </Suspense>
    )
}
